import React, { useState, useRef } from 'react';
import './ImportBagsButton.css';

// Splits CSV text into rows of cells. Quoted fields are respected because school names and
// districts in these order sheets regularly contain commas.
const parseCsvRows = (text) => {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];

    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      row.push(field);
      field = '';
    } else if (char === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else if (char !== '\r') {
      field += char;
    }
  }

  row.push(field);
  rows.push(row);

  return rows;
};

// The order sheets start with School Name / District / Order # lines, so the bag rows only begin
// after the "Product Serial #" header row.
const parseBagsCsv = (text) => {
  const rows = parseCsvRows(text).filter(row => row.some(cell => cell.trim() !== ''));

  const headerIndex = rows.findIndex(row => row.some(cell => cell.trim().toLowerCase().includes('serial')));
  if (headerIndex === -1) {
    return { error: 'Could not find a "Product Serial #" header row in this CSV.' };
  }

  // The sheet's own "Order #" line is the invoice number staff would otherwise retype, so it
  // seeds the form rather than being thrown away with the rest of the preamble.
  const orderRow = rows.slice(0, headerIndex)
    .find(row => (row[0] || '').trim().toLowerCase().startsWith('order'));
  const orderNumber = orderRow
    ? (orderRow.slice(1).map(cell => cell.trim()).find(cell => cell !== '') || '')
    : '';

  const header = rows[headerIndex].map(cell => cell.trim().toLowerCase());
  const serialIndex = header.findIndex(cell => cell.includes('serial'));
  const modelIndex = header.findIndex(cell => cell.includes('model'));
  const brandIndex = header.findIndex(cell => cell.includes('brand'));
  const rfidIndex = header.findIndex(cell => cell.includes('rfid'));

  const cellAt = (row, index) => (index >= 0 && row[index] ? row[index].trim() : '');

  const bags = [];
  const warnings = [];
  const incomplete = [];

  rows.slice(headerIndex + 1).forEach((row, i) => {
    const serialNumber = cellAt(row, serialIndex);
    if (!serialNumber) {
      warnings.push(`Row ${headerIndex + i + 2}: skipped, no serial number`);
      return;
    }

    const model = cellAt(row, modelIndex);
    const brand = cellAt(row, brandIndex);

    // Model and brand are only used for serials the warehouse hasn't scanned in yet, but for those
    // the CSV is the only source, and a blank model costs the unit its catalog name and photo.
    if (!model || !brand) {
      incomplete.push(serialNumber);
    }

    bags.push({
      serialNumber,
      model,
      brand,
      rfid: cellAt(row, rfidIndex)
    });
  });

  if (bags.length === 0) {
    return { error: 'No bag rows found below the "Product Serial #" header row.' };
  }

  return { bags, warnings, incomplete, orderNumber };
};

// Batch import of an order sheet, used from both the school's bags tab and a district's header.
// Pass schoolId to file the units at that school, or districtId to park them in the district's
// pool for distribution; the optional order number files them under the invoice they shipped on.
function ImportBagsButton({
  schoolId = null,
  districtId = null,
  targetName = '',
  label = 'Import Bags CSV',
  className = 'import-bags-button',
  onImported
}) {
  const [showModal, setShowModal] = useState(false);
  const [fileName, setFileName] = useState('');
  const [bags, setBags] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [incomplete, setIncomplete] = useState([]);
  const [orderNumber, setOrderNumber] = useState('');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const destination = schoolId != null
    ? 'this school'
    : (targetName ? `${targetName}'s pool` : "the district's pool");

  const handleFileSelected = (e) => {
    const file = e.target.files[0];

    // Clear the input so picking the same file again still fires a change event.
    e.target.value = '';

    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseBagsCsv(reader.result);

      if (parsed.error) {
        alert(parsed.error);
        return;
      }

      setFileName(file.name);
      setBags(parsed.bags);
      setWarnings(parsed.warnings);
      setIncomplete(parsed.incomplete);
      setOrderNumber(parsed.orderNumber);
      setResult(null);
      setShowModal(true);
    };
    reader.onerror = () => {
      console.error('Error reading CSV file:', reader.error);
      alert('Could not read that file. Please try again.');
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    setImporting(true);

    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');

      const requestBody = {
        schoolId: schoolId != null ? Number(schoolId) : null,
        districtId: districtId != null ? Number(districtId) : null,
        orderNumber: orderNumber.trim() || null,
        bags
      };

      const response = await fetch(`${apiEndpoint}/admin/batch-add-bags`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa(`${username}:${password}`)
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        setResult(await response.json());

        if (onImported) {
          await onImported();
        }
      } else {
        console.error('Failed to import bags:', response.status);

        let errorMessage = 'Failed to import bags. Please try again.';
        try {
          const errorData = await response.json();
          if (errorData && errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (e) {
          console.error('Could not parse error response:', e);
        }

        alert(errorMessage);
      }
    } catch (error) {
      console.error('Error importing bags:', error);
      alert('Error importing bags. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    if (importing) return;

    setShowModal(false);
    setFileName('');
    setBags([]);
    setWarnings([]);
    setIncomplete([]);
    setOrderNumber('');
    setResult(null);
  };

  const misplaced = (result && result.differentSchool) || [];
  const otherOrder = (result && result.differentOrder) || [];

  return (
    <>
      <button onClick={() => fileInputRef.current.click()} className={className}>
        {label}
      </button>
      <input
        type="file"
        ref={fileInputRef}
        accept=".csv,text/csv"
        onChange={handleFileSelected}
        style={{ display: 'none' }}
      />

      {showModal && (
        <div className="modal-overlay" onClick={handleClose}>
          <div className="modal-content wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{result ? 'Import Complete' : 'Import Bags from CSV'}</h3>
              <button className="modal-close" onClick={handleClose}>×</button>
            </div>
            <div className="modal-body">
              {result ? (
                <div className="import-result">
                  <p className="import-summary">
                    <strong>{result.successCount}</strong> of {bags.length} bag
                    {bags.length === 1 ? '' : 's'} filed under {destination}. Serials the warehouse
                    had not scanned in yet were created.
                  </p>
                  {result.orderNumber && (
                    <p className="import-summary">
                      Order <strong>{result.orderNumber}</strong>{' '}
                      {result.orderCreated ? 'was created for this import.' : 'already existed and was used as-is.'}
                    </p>
                  )}
                  {misplaced.length > 0 && (
                    <div className="import-issue">
                      <strong>
                        {schoolId != null
                          ? `Skipped, already assigned to another school (${misplaced.length}):`
                          : `Skipped, already at a school outside this district (${misplaced.length}):`}
                      </strong>
                      <div className="import-issue-serials">{misplaced.join(', ')}</div>
                    </div>
                  )}
                  {otherOrder.length > 0 && (
                    <div className="import-issue">
                      <strong>Left on the order they were already on ({otherOrder.length}):</strong>
                      <div className="import-issue-serials">
                        {otherOrder.join(', ')} — these were still filed under {destination}, but
                        moving a serial between orders is a transfer, not an import.
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <p className="import-summary">
                    <strong>{bags.length}</strong> bag{bags.length === 1 ? '' : 's'} found in{' '}
                    <em>{fileName}</em>. Serials already in the system are moved to {destination};
                    the rest are created from the model and brand below.
                  </p>
                  <div className="form-group">
                    <label htmlFor="import-order-number">Order number (optional)</label>
                    <input
                      type="text"
                      id="import-order-number"
                      value={orderNumber}
                      onChange={(e) => setOrderNumber(e.target.value)}
                      placeholder="The number on the invoice"
                    />
                    <small className="import-field-hint">
                      Files these units under that order, creating it if we have never seen it.
                      Prefilled from the sheet's "Order #" row when it has one.
                    </small>
                  </div>
                  {warnings.length > 0 && (
                    <div className="import-issue">
                      <strong>Skipped rows ({warnings.length}):</strong>
                      <div className="import-issue-serials">{warnings.join('; ')}</div>
                    </div>
                  )}
                  {incomplete.length > 0 && (
                    <div className="import-issue">
                      <strong>Missing model or brand ({incomplete.length}):</strong>
                      <div className="import-issue-serials">
                        {incomplete.join(', ')} — fine if these serials already exist, otherwise
                        they will be created without a catalog match.
                      </div>
                    </div>
                  )}
                  <div className="csv-preview-container">
                    <table className="csv-preview-table">
                      <thead>
                        <tr>
                          <th>Serial #</th>
                          <th>Model</th>
                          <th>Brand</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bags.map((bag, i) => (
                          <tr key={i}>
                            <td>{bag.serialNumber}</td>
                            <td>{bag.model || '-'}</td>
                            <td>{bag.brand || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              {result ? (
                <button className="modal-button submit" onClick={handleClose}>
                  Done
                </button>
              ) : (
                <>
                  <button className="modal-button cancel" onClick={handleClose} disabled={importing}>
                    Cancel
                  </button>
                  <button
                    className="modal-button submit"
                    onClick={handleImport}
                    disabled={importing || bags.length === 0}
                  >
                    {importing ? 'Importing...' : `Import ${bags.length} Bag${bags.length === 1 ? '' : 's'}`}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ImportBagsButton;
